import PercentageChart from '@components/charts/AudiencePercentageChart';
import IndexChart from '@components/charts/IndexChart';
import SourceKeys from '@components/charts/SourceKeys';
import UniverseChart from '@components/charts/UniverseChart';
import config from '@config';
import { Metric } from '@enums';
import { formatMetricLabel, generateKey } from '@utils';
import { useState } from 'react';
import type { ChartData } from 'types/common';
import Styled from './HorizontalBarChart.styled';

export interface HorizontalBarChartProps {
  colors: readonly string[];
  data: ChartData[];
  metric: Metric;
  hasMultipleAudiences?: boolean;
}

function HorizontalBarChart({
  colors,
  data,
  metric,
  hasMultipleAudiences = false
}: HorizontalBarChartProps) {
  const [dataItemHeight, setDataItemHeight] = useState(0);

  const setDataItemRef = (element: HTMLElement | null) => {
    if (element) {
      const { height } = element.getBoundingClientRect();

      setDataItemHeight(height);
    }
  };

  const calcBarChartHeight = () => {
    let chartRowHeight = dataItemHeight || config.chart.row.defaultHeight;

    if (chartRowHeight >= config.chart.row.maxHeight) {
      chartRowHeight = config.chart.row.maxHeight;
    }

    return data.length * chartRowHeight;
  };

  const renderDataPoints = () => {
    if (hasMultipleAudiences) {
      return (
        <Styled.DataListItem data-testid="chart-data-list-item-title">
          {data[0]?.name.split('✂️').shift()}
        </Styled.DataListItem>
      );
    }

    return (
      <Styled.DataList>
        {data.map(({ name, value }) => (
          <Styled.DataListItem
            ref={setDataItemRef}
            key={generateKey(`chart-datapoint-${name}-${value}`)}>
            {name}
          </Styled.DataListItem>
        ))}
      </Styled.DataList>
    );
  };

  const chartKeyLabels = (() => {
    if (hasMultipleAudiences) {
      return data
        .map((item) => item.audience?.title)
        .filter((title): title is string => Boolean(title));
    }

    const audienceTitle = data[0]?.audience?.title;

    if (audienceTitle) {
      return [audienceTitle];
    }

    if (data.length > 0) {
      return ['All internet users'];
    }

    return [];
  })();

  const chartKeyColors = chartKeyLabels.length === 1 ? [colors[0]] : colors;

  const height = calcBarChartHeight();
  const gridHorizontal = hasMultipleAudiences;
  const chartProps = {
    barSize: 16,
    colors,
    data,
    gridHorizontal,
    height,
    compact: false,
    showYAxis: true
  };

  const getChart = () => {
    if (metric === Metric.Index) {
      return <IndexChart {...chartProps} />;
    }

    if (metric === Metric.Universe) {
      return <UniverseChart {...chartProps} />;
    }

    return <PercentageChart {...chartProps} />;
  };

  return (
    <Styled.BarChartSection>
      <Styled.BarChartLayout data-testid="chart-bar-table">
        <Styled.LabelColumn>
          <Styled.LabelHeader />
          {renderDataPoints()}
        </Styled.LabelColumn>
        <Styled.ChartColumn>
          <Styled.ChartHeader>
            <Styled.MetricLabel data-testid="chart-bar-table-metric">
              {formatMetricLabel(metric)}
            </Styled.MetricLabel>
          </Styled.ChartHeader>
          <Styled.ChartArea data-testid="chart-bar-table-chart">
            {getChart()}
            {!hasMultipleAudiences &&
              Array.from({ length: data.length }).map((_, index) => (
                <Styled.Separator
                  x={0}
                  y={(index + 1) * dataItemHeight}
                  key={generateKey(`chart-separator-${metric}-${index}`)}
                />
              ))}
          </Styled.ChartArea>
        </Styled.ChartColumn>
      </Styled.BarChartLayout>
      <SourceKeys
        title="Audience:"
        colors={chartKeyColors}
        labels={chartKeyLabels}
      />
    </Styled.BarChartSection>
  );
}

export default HorizontalBarChart;
